import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiX } from 'react-icons/fi';
import { TERMS_HUMAN_SUMMARY, TERMS_LAST_UPDATED, TERMS_SECTIONS } from './termsContent';

export const LEGAL_TERMS_VERSION = 'v1';
export const LEGAL_TERMS_STORAGE_KEY = `myhoommy_terms_accepted_${LEGAL_TERMS_VERSION}`;

const LegalTermsModal = ({
    isOpen,
    onClose,
    onAccept,
    showCloseButton = false,
    persistOnAccept = false,
    title = 'Terminos y Condiciones de Uso',
}) => {
    const contentRef = useRef(null);
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
    const [acceptedCheck, setAcceptedCheck] = useState(false);

    const canContinue = useMemo(() => hasScrolledToEnd && acceptedCheck, [hasScrolledToEnd, acceptedCheck]);

    useEffect(() => {
        if (!isOpen) return;
        setHasScrolledToEnd(false);
        setAcceptedCheck(false);

        const container = contentRef.current;
        if (!container) return;

        const handleScroll = () => {
            const reachedBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 8;
            if (reachedBottom) setHasScrolledToEnd(true);
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => container.removeEventListener('scroll', handleScroll);
    }, [isOpen]);

    const handleAccept = () => {
        if (!canContinue) return;

        if (persistOnAccept) {
            localStorage.setItem(LEGAL_TERMS_STORAGE_KEY, 'accepted');
        }

        if (onAccept) onAccept();
        if (onClose) onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-4xl card overflow-hidden">
                <header className="px-6 py-5 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-900 flex items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg sm:text-xl font-bold text-surface-900 dark:text-white">{title}</h2>
                        <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">Ultima actualizacion: {TERMS_LAST_UPDATED}</p>
                    </div>
                    {showCloseButton && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-500"
                            aria-label="Cerrar"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    )}
                </header>

                <div ref={contentRef} className="max-h-[62vh] overflow-y-auto px-6 py-5 space-y-6">
                    <p className="text-sm text-surface-600 dark:text-surface-300 leading-relaxed">
                        Al usar MyHoommy aceptas estos terminos y la{' '}
                        <Link to="/privacidad" className="text-brand-500 hover:text-brand-400 font-semibold" target="_blank" rel="noreferrer">
                            Politica de Privacidad
                        </Link>
                        .
                    </p>

                    {TERMS_SECTIONS.map((section) => (
                        <section key={section.title} className="space-y-3">
                            <h3 className="text-base sm:text-lg font-bold text-surface-900 dark:text-white">{section.title}</h3>
                            {section.paragraphs?.map((text) => (
                                <p key={text} className="text-sm text-surface-600 dark:text-surface-300 leading-relaxed">{text}</p>
                            ))}
                            {section.bullets?.length > 0 && (
                                <ul className="list-disc list-inside space-y-1.5 text-sm text-surface-600 dark:text-surface-300">
                                    {section.bullets.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    ))}

                    <section className="rounded-xl border border-brand-200 dark:border-brand-700/40 bg-brand-50/70 dark:bg-brand-500/10 p-4 space-y-3">
                        <h3 className="text-base sm:text-lg font-bold text-brand-800 dark:text-brand-300">
                            Resumen para humanos
                        </h3>
                        <ul className="space-y-2 text-sm text-brand-900 dark:text-brand-200">
                            {TERMS_HUMAN_SUMMARY.map((point) => (
                                <li key={point} className="flex items-start gap-2">
                                    <FiCheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>

                <footer className="px-6 py-4 border-t border-surface-200 dark:border-surface-800 bg-white dark:bg-surface-900 space-y-3">
                    <label className="flex items-start gap-2 text-sm text-surface-600 dark:text-surface-300">
                        <input
                            type="checkbox"
                            className="mt-0.5 w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-brand-500 focus:ring-brand-500"
                            checked={acceptedCheck}
                            onChange={(e) => setAcceptedCheck(e.target.checked)}
                        />
                        <span>
                            He leido y acepto los <strong>Terminos y Condiciones</strong> y la <strong>Politica de Privacidad</strong> de MyHoommy.
                        </span>
                    </label>

                    {!hasScrolledToEnd && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                            Para continuar, desplaza el contenido hasta el final.
                        </p>
                    )}

                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleAccept}
                            disabled={!canContinue}
                            className="btn-primary px-5 py-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Aceptar y continuar
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default LegalTermsModal;
